import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { HiOutlineX } from 'react-icons/hi';
import { HiOutlineMapPin, HiOutlineBuildingStorefront, HiOutlineEye, HiOutlineShieldCheck, HiOutlineCloud, HiOutlineBuildingOffice2, HiOutlineShoppingCart, HiOutlineChartBar, HiOutlineSquare3Stack3D } from 'react-icons/hi2';
import { MdChevronRight } from 'react-icons/md';
import CustomDropdown from '../../components/CustomDropdown/CustomDropdown';
import CustomDropdownWithSearch from '../../components/CustomDropdownWithSearch/CustomDropdownWithSearch';
import MultiSelectDropdown from '../../components/MultiSelectDropdown/MultiSelectDropdown';
import RangeSlider from '../../components/RangeSlider/RangeSlider';
import Tooltip from '../../components/Tooltip/Tooltip';
import { fetchStates, fetchAllCitiesByState, fetchAllZipcodes } from '../../services/geographicalService';
import { fetchBrandsByRetailer, fetchAllChannels, fetchAllRetailersWithChannels } from '../../services/mapPageService';
import type { RetailerWithChannels } from '../../services/mapPageService';
import { normalizeChannels } from '../../utils/channelNormalizer';
import './Filter.css';

interface FilterProps {
  onFilterChange: (filters: FilterState) => void;
}

export type ViewingMode = 'all' | 'whitespace' | 'recommended';

export interface FilterState {
  states?: string[];
  cities?: string[];
  zipcodes?: string[];
  retailers?: string[];
  brands?: string[];
  categories?: string[];
  solutions?: string[];
  whitespace?: string[];
  recommendedSolutions?: string[];
  viewingMode?: ViewingMode;
  penetrationMin?: number;
  penetrationMax?: number;
  crimeIndex?: string[];
  weatherHotMonthsMin?: number;
  weatherHotMonthsMax?: number;
  weatherColdMonthsMin?: number;
  weatherColdMonthsMax?: number;
  shoppingComplexOnly?: boolean;
  cartOrdinance?: string;
  showNeighbourhood?: boolean;
  neighbourhoodRetailers?: string[];
  neighbourhoodChannels?: string[];
  neighbourhoodStatus?: string;
  neighbourhoodSolutions?: string[];
  neighbourhoodWhitespace?: string[];
  neighbourhoodAnonymous?: boolean;
}

const CRIME_INDEX_OPTIONS = [
  { id: 'low', label: 'Low' },
  { id: 'medium', label: 'Medium' },
  { id: 'high', label: 'High' },
  { id: 'critical', label: 'Critical' },
];

const SOLUTIONS_OPTIONS = ['Purchek', 'Cart Containment', 'Cart Manager', 'Subscription', 'Paired Wheel', 'Wheel Reorder', 'Pending Service'];

const NEIGHBOURHOOD_STATUS_OPTIONS = ['All', 'Customers', 'Non-Customers'];

const CART_ORDINANCE_OPTIONS = ['All', 'No Ordinance', 'General Recommendation', 'Penalty for Cart Owner'];

const CACHE_KEY = 'region-filter-settings';

const loadCachedFilters = () => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) return JSON.parse(cached);
  } catch { /* ignore */ }
  return null;
};

const saveCachedFilters = (filters: Record<string, any>) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(filters));
  } catch { /* ignore */ }
};

const Filter: React.FC<FilterProps> = ({ onFilterChange }) => {
  const cached = useMemo(() => loadCachedFilters(), []);

  const [viewingMode, setViewingMode] = useState<ViewingMode>(cached?.viewingMode || 'recommended');
  const [states, setStates] = useState<string[]>(cached?.states || []);
  const [cities, setCities] = useState<string[]>(cached?.cities || []);
  const [zipcodes, setZipcodes] = useState<string[]>(cached?.zipcodes || []);
  const [retailers, setRetailers] = useState<string[]>(cached?.retailers || []);
  const [brands, setBrands] = useState<string[]>(cached?.brands || []);
  const [categories, setCategories] = useState<string[]>(cached?.categories || []);
  const [solutions, setSolutions] = useState<string[]>(cached?.solutions || []);
  const [whitespace, setWhitespace] = useState<string[]>(cached?.whitespace || []);
  const [recommendedSolutions, setRecommendedSolutions] = useState<string[]>(cached?.recommendedSolutions || []);

  // Advanced filters
  const [penetrationMin, setPenetrationMin] = useState(cached?.penetrationMin ?? 0);
  const [penetrationMax, setPenetrationMax] = useState(cached?.penetrationMax ?? 100);
  const [crimeIndex, setCrimeIndex] = useState<string[]>(cached?.crimeIndex || []);
  const [hotMonthsMin, setHotMonthsMin] = useState(cached?.hotMonthsMin ?? 0);
  const [hotMonthsMax, setHotMonthsMax] = useState(cached?.hotMonthsMax ?? 12);
  const [coldMonthsMin, setColdMonthsMin] = useState(cached?.coldMonthsMin ?? 0);
  const [coldMonthsMax, setColdMonthsMax] = useState(cached?.coldMonthsMax ?? 12);
  const [shoppingComplexOnly, setShoppingComplexOnly] = useState(cached?.shoppingComplexOnly ?? false);
  const [cartOrdinance, setCartOrdinance] = useState(cached?.cartOrdinance || 'All');
  const [showNeighbourhood, setShowNeighbourhood] = useState(cached?.showNeighbourhood ?? false);
  const [neighbourhoodRetailers, setNeighbourhoodRetailers] = useState<string[]>(cached?.neighbourhoodRetailers || []);
  const [neighbourhoodChannels, setNeighbourhoodChannels] = useState<string[]>(cached?.neighbourhoodChannels || []);
  const [neighbourhoodStatus, setNeighbourhoodStatus] = useState(cached?.neighbourhoodStatus || 'All');
  const [neighbourhoodSolutions, setNeighbourhoodSolutions] = useState<string[]>(cached?.neighbourhoodSolutions || []);
  const [neighbourhoodWhitespace, setNeighbourhoodWhitespace] = useState<string[]>(cached?.neighbourhoodWhitespace || []);
  const [neighbourhoodAnonymous, setNeighbourhoodAnonymous] = useState(cached?.neighbourhoodAnonymous ?? false);

  // Collapsible sections
  const [isPenetrationExpanded, setIsPenetrationExpanded] = useState(false);
  const [isCrimeExpanded, setIsCrimeExpanded] = useState(false);
  const [isWeatherExpanded, setIsWeatherExpanded] = useState(false);
  const [isShoppingExpanded, setIsShoppingExpanded] = useState(false);
  const [isCartExpanded, setIsCartExpanded] = useState(false);

  const [availableStates, setAvailableStates] = useState<string[]>([]);
  const [availableZipcodes, setAvailableZipcodes] = useState<string[]>([]);
  const [cityMap, setCityMap] = useState<Record<string, string[]>>({});
  const [allRetailersWithChannels, setAllRetailersWithChannels] = useState<RetailerWithChannels[]>([]);
  const [brandMap, setBrandMap] = useState<Record<string, string[]>>({});
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);

  useEffect(() => {
    fetchStates().then(setAvailableStates);
    fetchAllCitiesByState().then(setCityMap);
    fetchAllZipcodes().then(setAvailableZipcodes);
    fetchAllRetailersWithChannels().then(setAllRetailersWithChannels);
    fetchAllChannels().then(channels => setAvailableCategories(normalizeChannels(channels)));
  }, []);

  // Filter retailers in frontend based on selected channels
  const availableRetailers = useMemo(() => {
    if (categories.length === 0) {
      return allRetailersWithChannels.map(r => r.ultimate_parent);
    }
    return allRetailersWithChannels
      .filter(r => r.channels.some(ch => categories.includes(ch)))
      .map(r => r.ultimate_parent);
  }, [allRetailersWithChannels, categories]);

  // Fetch brands when retailers change
  useEffect(() => {
    if (retailers.length === 0) {
      setBrandMap({});
      return;
    }
    const fetchBrands = async () => {
      const map: Record<string, string[]> = {};
      await Promise.all(
        retailers.map(async (r) => {
          const b = await fetchBrandsByRetailer(r);
          map[r] = b;
        })
      );
      setBrandMap(map);
    };
    fetchBrands();
  }, [retailers]);

  // All cities across all states
  const allCities = useMemo(() => {
    const all = Object.values(cityMap).flat();
    return Array.from(new Set(all)).sort((a, b) => a.localeCompare(b));
  }, [cityMap]);

  // Dependent filtering: cities filtered by selected states
  const filteredCities = useMemo(() => {
    if (states.length === 0) return allCities;
    const citySet = new Set<string>();
    states.forEach((s) => {
      (cityMap[s] || []).forEach((c) => citySet.add(c));
    });
    return Array.from(citySet).sort((a, b) => a.localeCompare(b));
  }, [states, cityMap, allCities]);

  // All brands across selected retailers
  const filteredBrands = useMemo(() => {
    if (retailers.length === 0) return [];
    const brandSet = new Set<string>();
    retailers.forEach((r) => {
      (brandMap[r] || []).forEach((b) => brandSet.add(b));
    });
    return Array.from(brandSet).sort((a, b) => a.localeCompare(b));
  }, [retailers, brandMap]);

  const handleStatesChange = (newStates: string[]) => {
    setStates(newStates);
    setCities([]);
  };

  const handleCitiesChange = (newCities: string[]) => {
    setCities(newCities);
  };

  const handleRetailersChange = (newRetailers: string[]) => {
    setRetailers(newRetailers);
    setBrands([]);
    if (newRetailers.length === 0) {
      setShowNeighbourhood(false);
      setNeighbourhoodRetailers([]);
    }
  };

  const handleBrandsChange = (newBrands: string[]) => {
    setBrands(newBrands);
  };

  const handleCategoriesChange = (newCategories: string[]) => {
    setCategories(newCategories);
    setRetailers([]);
    setBrands([]);
  };

  const buildFilterState = (overrides?: Partial<FilterState>): FilterState => ({
    states: states.length > 0 ? states : undefined,
    cities: cities.length > 0 ? cities : undefined,
    zipcodes: zipcodes.length > 0 ? zipcodes : undefined,
    retailers: retailers.length > 0 ? retailers : undefined,
    brands: brands.length > 0 ? brands : undefined,
    categories: categories.length > 0 ? categories : undefined,
    solutions: solutions.length > 0 ? solutions : undefined,
    whitespace: whitespace.length > 0 ? whitespace : undefined,
    recommendedSolutions: recommendedSolutions.length > 0 ? recommendedSolutions : undefined,
    viewingMode,
    penetrationMin: penetrationMin > 0 ? penetrationMin : undefined,
    penetrationMax: penetrationMax < 100 ? penetrationMax : undefined,
    crimeIndex: crimeIndex.length > 0 ? crimeIndex : undefined,
    weatherHotMonthsMin: hotMonthsMin > 0 ? hotMonthsMin : undefined,
    weatherHotMonthsMax: hotMonthsMax < 12 ? hotMonthsMax : undefined,
    weatherColdMonthsMin: coldMonthsMin > 0 ? coldMonthsMin : undefined,
    weatherColdMonthsMax: coldMonthsMax < 12 ? coldMonthsMax : undefined,
    shoppingComplexOnly: shoppingComplexOnly || undefined,
    cartOrdinance: cartOrdinance !== 'All' ? cartOrdinance : undefined,
    showNeighbourhood: showNeighbourhood || undefined,
    neighbourhoodRetailers: neighbourhoodRetailers.length > 0 ? neighbourhoodRetailers : undefined,
    neighbourhoodChannels: neighbourhoodChannels.length > 0 ? neighbourhoodChannels : undefined,
    neighbourhoodStatus: neighbourhoodStatus !== 'All' ? neighbourhoodStatus : undefined,
    neighbourhoodSolutions: neighbourhoodSolutions.length > 0 ? neighbourhoodSolutions : undefined,
    neighbourhoodWhitespace: neighbourhoodWhitespace.length > 0 ? neighbourhoodWhitespace : undefined,
    neighbourhoodAnonymous: neighbourhoodAnonymous || undefined,
    ...overrides,
  });

  const handleViewingModeChange = (mode: ViewingMode) => {
    setViewingMode(mode);
    onFilterChange(buildFilterState({ viewingMode: mode }));
    // Save viewing mode change to cache immediately
    saveCachedFilters({
      viewingMode: mode, states, cities, zipcodes, retailers, brands, categories, solutions, whitespace, recommendedSolutions,
      penetrationMin, penetrationMax, crimeIndex,
      hotMonthsMin, hotMonthsMax, coldMonthsMin, coldMonthsMax,
      shoppingComplexOnly, cartOrdinance, showNeighbourhood, neighbourhoodRetailers, neighbourhoodChannels, neighbourhoodStatus, neighbourhoodSolutions, neighbourhoodWhitespace, neighbourhoodAnonymous,
    });
  };

  const handleApplyFilters = () => {
    const filterState = buildFilterState();
    onFilterChange(filterState);
    lastAppliedRef.current = getFilterSnapshot();
    saveCachedFilters({
      viewingMode, states, cities, zipcodes, retailers, brands, categories, solutions, whitespace, recommendedSolutions,
      penetrationMin, penetrationMax, crimeIndex,
      hotMonthsMin, hotMonthsMax, coldMonthsMin, coldMonthsMax,
      shoppingComplexOnly, cartOrdinance, showNeighbourhood, neighbourhoodRetailers, neighbourhoodChannels, neighbourhoodStatus, neighbourhoodSolutions, neighbourhoodWhitespace, neighbourhoodAnonymous,
    });
  };

  const handleClearAllFilters = () => {
    setViewingMode('recommended');
    setStates([]);
    setCities([]);
    setZipcodes([]);
    setRetailers([]);
    setBrands([]);
    setCategories([]);
    setSolutions([]);
    setWhitespace([]);
    setRecommendedSolutions([]);
    setPenetrationMin(0);
    setPenetrationMax(100);
    setCrimeIndex([]);
    setHotMonthsMin(0);
    setHotMonthsMax(12);
    setColdMonthsMin(0);
    setColdMonthsMax(12);
    setShoppingComplexOnly(false);
    setCartOrdinance('All');
    setShowNeighbourhood(false);
    setNeighbourhoodRetailers([]);
    setNeighbourhoodChannels([]);
    setNeighbourhoodStatus('All');
    setNeighbourhoodSolutions([]);
    setNeighbourhoodWhitespace([]);
    setNeighbourhoodAnonymous(false);
    onFilterChange({ viewingMode: 'recommended' });
    lastAppliedRef.current = JSON.stringify({
      states: [], cities: [], zipcodes: [], retailers: [], brands: [], categories: [], solutions: [], whitespace: [], recommendedSolutions: [],
      neighbourhoodRetailers: [], neighbourhoodChannels: [], neighbourhoodStatus: 'All', neighbourhoodSolutions: [], neighbourhoodWhitespace: [],
      penetrationMin: 0, penetrationMax: 100, crimeIndex: [],
      hotMonthsMin: 0, hotMonthsMax: 12, coldMonthsMin: 0, coldMonthsMax: 12,
      shoppingComplexOnly: false, cartOrdinance: 'All', showNeighbourhood: false,
    });
    sessionStorage.removeItem(CACHE_KEY);
  };

  const hasAdvancedFilters = penetrationMin > 0 || penetrationMax < 100 || crimeIndex.length > 0 ||
    hotMonthsMin > 0 || hotMonthsMax < 12 || coldMonthsMin > 0 || coldMonthsMax < 12 ||
    shoppingComplexOnly || cartOrdinance !== 'All';

  const hasActiveFilters = states.length > 0 || cities.length > 0 || zipcodes.length > 0 || retailers.length > 0 || brands.length > 0 || categories.length > 0 || solutions.length > 0 || whitespace.length > 0 || recommendedSolutions.length > 0 || neighbourhoodRetailers.length > 0 || neighbourhoodChannels.length > 0 || neighbourhoodStatus !== 'All' || neighbourhoodSolutions.length > 0 || neighbourhoodWhitespace.length > 0 || hasAdvancedFilters;

  // Track last applied filter snapshot to enable/disable Apply button
  const getFilterSnapshot = useCallback(() => JSON.stringify({
    states, cities, zipcodes, retailers, brands, categories, solutions, whitespace, recommendedSolutions,
    neighbourhoodRetailers, neighbourhoodChannels, neighbourhoodStatus, neighbourhoodSolutions, neighbourhoodWhitespace,
    penetrationMin, penetrationMax, crimeIndex,
    hotMonthsMin, hotMonthsMax, coldMonthsMin, coldMonthsMax,
    shoppingComplexOnly, cartOrdinance, showNeighbourhood, neighbourhoodAnonymous,
  }), [states, cities, zipcodes, retailers, brands, categories, solutions, whitespace, recommendedSolutions, neighbourhoodRetailers, neighbourhoodChannels, neighbourhoodStatus, neighbourhoodSolutions, neighbourhoodWhitespace, penetrationMin, penetrationMax, crimeIndex,
    hotMonthsMin, hotMonthsMax, coldMonthsMin, coldMonthsMax, shoppingComplexOnly, cartOrdinance, showNeighbourhood, neighbourhoodAnonymous]);

  const lastAppliedRef = useRef<string>(getFilterSnapshot());
  const hasUnappliedChanges = getFilterSnapshot() !== lastAppliedRef.current;

  // Apply cached filters on mount
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current && cached) {
      mountedRef.current = true;
      const restoredFilters = buildFilterState();
      // Only apply if there were actual filters cached
      if (hasActiveFilters) {
        onFilterChange(restoredFilters);
        lastAppliedRef.current = getFilterSnapshot();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="filter-container">
      {hasActiveFilters && (
        <div className="clear-filters-section">
          <button className="clear-all-filters-btn" onClick={handleClearAllFilters}>
            <HiOutlineX />
            Clear All Filters
          </button>
        </div>
      )}

      <div className="rf-filter-section">
        {hasActiveFilters && (
          <div className="active-filters-display">
            <div className="active-filters-title">Active Filters</div>
            <div className="active-filters-list">
              {states.length > 0 && (
                <span className="active-filter-chip">
                  States: {states.length === availableStates.length ? 'All' : states.join(', ')}
                  <button className="filter-chip-remove" onClick={() => handleStatesChange([])} aria-label="Remove States filter"><HiOutlineX /></button>
                </span>
              )}
              {cities.length > 0 && (
                <span className="active-filter-chip">
                  Cities: {cities.length === filteredCities.length ? 'All' : cities.join(', ')}
                  <button className="filter-chip-remove" onClick={() => setCities([])} aria-label="Remove Cities filter"><HiOutlineX /></button>
                </span>
              )}
              {zipcodes.length > 0 && (
                <span className="active-filter-chip">
                  Zip Codes: {zipcodes.join(', ')}
                  <button className="filter-chip-remove" onClick={() => setZipcodes([])} aria-label="Remove Zip Codes filter"><HiOutlineX /></button>
                </span>
              )}
              {retailers.length > 0 && (
                <span className="active-filter-chip">
                  Retailers: {retailers.length === availableRetailers.length ? 'All' : retailers.join(', ')}
                  <button className="filter-chip-remove" onClick={() => handleRetailersChange([])} aria-label="Remove Retailers filter"><HiOutlineX /></button>
                </span>
              )}
              {brands.length > 0 && (
                <span className="active-filter-chip">
                  Brands: {brands.length === filteredBrands.length ? 'All' : brands.join(', ')}
                  <button className="filter-chip-remove" onClick={() => setBrands([])} aria-label="Remove Brands filter"><HiOutlineX /></button>
                </span>
              )}
              {categories.length > 0 && (
                <span className="active-filter-chip">
                  Channel: {categories.length === availableCategories.length ? 'All' : categories.join(', ')}
                  <button className="filter-chip-remove" onClick={() => handleCategoriesChange([])} aria-label="Remove Channel filter"><HiOutlineX /></button>
                </span>
              )}
              {solutions.length > 0 && (
                <span className="active-filter-chip">
                  Solutions: {solutions.join(', ')}
                  <button className="filter-chip-remove" onClick={() => setSolutions([])} aria-label="Remove Solutions filter"><HiOutlineX /></button>
                </span>
              )}
              {whitespace.length > 0 && (
                <span className="active-filter-chip">
                  Whitespace: {whitespace.join(', ')}
                  <button className="filter-chip-remove" onClick={() => setWhitespace([])} aria-label="Remove Whitespace filter"><HiOutlineX /></button>
                </span>
              )}
              {recommendedSolutions.length > 0 && (
                <span className="active-filter-chip">
                  Recommended: {recommendedSolutions.join(', ')}
                  <button className="filter-chip-remove" onClick={() => setRecommendedSolutions([])} aria-label="Remove Recommended filter"><HiOutlineX /></button>
                </span>
              )}
              {neighbourhoodRetailers.length > 0 && (
                <span className="active-filter-chip">
                  Neighbourhood Retailers: {neighbourhoodRetailers.join(', ')}
                  <button className="filter-chip-remove" onClick={() => setNeighbourhoodRetailers([])} aria-label="Remove Neighbourhood Retailers filter"><HiOutlineX /></button>
                </span>
              )}
              {neighbourhoodChannels.length > 0 && (
                <span className="active-filter-chip">
                  Neighbourhood Channels: {neighbourhoodChannels.join(', ')}
                  <button className="filter-chip-remove" onClick={() => setNeighbourhoodChannels([])} aria-label="Remove Neighbourhood Channels filter"><HiOutlineX /></button>
                </span>
              )}
              {neighbourhoodStatus !== 'All' && (
                <span className="active-filter-chip">
                  Neighbourhood Status: {neighbourhoodStatus}
                  <button className="filter-chip-remove" onClick={() => setNeighbourhoodStatus('All')} aria-label="Remove Neighbourhood Status filter"><HiOutlineX /></button>
                </span>
              )}
              {neighbourhoodSolutions.length > 0 && (
                <span className="active-filter-chip">
                  Neighbourhood Solutions: {neighbourhoodSolutions.join(', ')}
                  <button className="filter-chip-remove" onClick={() => setNeighbourhoodSolutions([])} aria-label="Remove Neighbourhood Solutions filter"><HiOutlineX /></button>
                </span>
              )}
              {neighbourhoodWhitespace.length > 0 && (
                <span className="active-filter-chip">
                  Neighbourhood Whitespace: {neighbourhoodWhitespace.join(', ')}
                  <button className="filter-chip-remove" onClick={() => setNeighbourhoodWhitespace([])} aria-label="Remove Neighbourhood Whitespace filter"><HiOutlineX /></button>
                </span>
              )}
              {(penetrationMin > 0 || penetrationMax < 100) && (
                <span className="active-filter-chip">
                  Penetration: {penetrationMin}% - {penetrationMax}%
                  <button className="filter-chip-remove" onClick={() => { setPenetrationMin(0); setPenetrationMax(100); }} aria-label="Remove Penetration filter"><HiOutlineX /></button>
                </span>
              )}
              {crimeIndex.length > 0 && (
                <span className="active-filter-chip">
                  Crime: {crimeIndex.map(id => CRIME_INDEX_OPTIONS.find(o => o.id === id)?.label).join(', ')}
                  <button className="filter-chip-remove" onClick={() => setCrimeIndex([])} aria-label="Remove Crime filter"><HiOutlineX /></button>
                </span>
              )}
              {(hotMonthsMin > 0 || hotMonthsMax < 12 || coldMonthsMin > 0 || coldMonthsMax < 12) && (
                <span className="active-filter-chip">
                  Weather
                  <button className="filter-chip-remove" onClick={() => { setHotMonthsMin(0); setHotMonthsMax(12); setColdMonthsMin(0); setColdMonthsMax(12); }} aria-label="Remove Weather filter"><HiOutlineX /></button>
                </span>
              )}
              {shoppingComplexOnly && (
                <span className="active-filter-chip">
                  Shopping Complex
                  <button className="filter-chip-remove" onClick={() => setShoppingComplexOnly(false)} aria-label="Remove Shopping filter"><HiOutlineX /></button>
                </span>
              )}
              {cartOrdinance !== 'All' && (
                <span className="active-filter-chip">
                  Cart Ordinance: {cartOrdinance}
                  <button className="filter-chip-remove" onClick={() => setCartOrdinance('All')} aria-label="Remove Cart Ordinance filter"><HiOutlineX /></button>
                </span>
              )}
            </div>
          </div>
        )}

        <div className="filter-group">
          <div className="filter-group-header">
            <HiOutlineEye className="group-icon" />
            <span className="group-title">Viewing Mode</span>
          </div>
          <div className="viewing-mode-toggle">
            <Tooltip content="Show stores with system-recommended solutions based on readiness criteria" side="bottom">
              <button
                className={`viewing-mode-btn ${viewingMode === 'recommended' ? 'active' : ''}`}
                onClick={() => handleViewingModeChange('recommended')}
                type="button"
              >
                Recommended
              </button>
            </Tooltip>
            <Tooltip content="Show stores missing at least one solution (not yet installed)" side="bottom">
              <button
                className={`viewing-mode-btn ${viewingMode === 'whitespace' ? 'active' : ''}`}
                onClick={() => handleViewingModeChange('whitespace')}
                type="button"
              >
                Whitespace
              </button>
            </Tooltip>
            <Tooltip content="Show all stores regardless of solutions installed" side="bottom">
              <button
                className={`viewing-mode-btn ${viewingMode === 'all' ? 'active' : ''}`}
                onClick={() => handleViewingModeChange('all')}
                type="button"
              >
                All Stores
              </button>
            </Tooltip>
          </div>
        </div>

        <div className="filter-group">
          <div className="filter-group-header">
            <HiOutlineMapPin className="group-icon" />
            <span className="group-title">Region</span>
            {(states.length > 0 || cities.length > 0 || zipcodes.length > 0) && (
              <span className="group-filter-count">{states.length + cities.length + zipcodes.length}</span>
            )}
          </div>

          <div className="filter-group-body">
            <div className="filter-item">
              <Tooltip content="Filter stores by US state" side="right">
                <label className="filter-label">
                  State
                  {states.length > 0 && <span className="filter-count">{states.length}</span>}
                </label>
              </Tooltip>
              <CustomDropdownWithSearch
                value={states}
                options={availableStates}
                onChange={handleStatesChange}
                placeholder="All states"
                showTags={false}
              />
            </div>

            <div className="filter-item">
              <Tooltip content="Filter stores by city" side="right">
                <label className="filter-label">
                  City
                  {cities.length > 0 && <span className="filter-count">{cities.length}</span>}
                </label>
              </Tooltip>
              <CustomDropdownWithSearch
                value={cities}
                options={filteredCities}
                onChange={handleCitiesChange}
                placeholder="All cities"
                showTags={false}
              />
            </div>

            <div className="filter-item">
              <Tooltip content="Filter stores by zip code" side="right">
                <label className="filter-label">
                  Zip Code
                  {zipcodes.length > 0 && <span className="filter-count">{zipcodes.length}</span>}
                </label>
              </Tooltip>
              <CustomDropdownWithSearch
                value={zipcodes}
                options={availableZipcodes}
                onChange={setZipcodes}
                placeholder="All zip codes"
                showTags={false}
              />
            </div>
          </div>
        </div>

        <div className="filter-group">
          <div className="filter-group-header">
            <HiOutlineSquare3Stack3D className="group-icon" />
            <span className="group-title">Solutions</span>
            {(solutions.length > 0 || whitespace.length > 0 || recommendedSolutions.length > 0) && (
              <span className="group-filter-count">{solutions.length + whitespace.length + recommendedSolutions.length}</span>
            )}
          </div>
          <div className="filter-group-body">
            {viewingMode === 'recommended' ? (
              <div className="filter-item">
                <Tooltip content="Solutions recommended by the system based on store readiness" side="right">
                  <label className="filter-label">
                    Recommended Solutions
                    {recommendedSolutions.length > 0 && <span className="filter-count">{recommendedSolutions.length}</span>}
                  </label>
                </Tooltip>
                <MultiSelectDropdown
                  values={recommendedSolutions}
                  options={SOLUTIONS_OPTIONS}
                  onChange={setRecommendedSolutions}
                  placeholder="All solutions"
                  hasSelectAll
                />
              </div>
            ) : (
              <>
                <div className="filter-item">
                  <Tooltip content="Solutions currently installed at the store" side="right">
                    <label className="filter-label">
                      Installed Solutions
                      {solutions.length > 0 && <span className="filter-count">{solutions.length}</span>}
                    </label>
                  </Tooltip>
                  <MultiSelectDropdown
                    values={solutions}
                    options={SOLUTIONS_OPTIONS}
                    onChange={setSolutions}
                    placeholder="All solutions"
                    hasSelectAll
                    disabledOptions={whitespace}
                  />
                </div>
                <div className="filter-item">
                  <Tooltip content="Solutions not yet installed at the store" side="right">
                    <label className="filter-label">
                      Whitespace Solutions
                      {whitespace.length > 0 && <span className="filter-count">{whitespace.length}</span>}
                    </label>
                  </Tooltip>
                  <MultiSelectDropdown
                    values={whitespace}
                    options={SOLUTIONS_OPTIONS}
                    onChange={setWhitespace}
                    placeholder="All solutions"
                    hasSelectAll
                    disabledOptions={solutions}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="filter-group">
          <div className="filter-group-header">
            <HiOutlineBuildingStorefront className="group-icon" />
            <span className="group-title">Retailer</span>
            {(categories.length > 0 || retailers.length > 0 || brands.length > 0) && (
              <span className="group-filter-count">{categories.length + retailers.length + brands.length}</span>
            )}
          </div>

          <div className="filter-group-body">
            <div className="filter-item">
              <Tooltip content="Filter retailers and brands by store channel" side="right">
                <label className="filter-label">
                  Channel
                  {categories.length > 0 && <span className="filter-count">{categories.length}</span>}
                </label>
              </Tooltip>
              <MultiSelectDropdown
                values={categories}
                options={availableCategories}
                onChange={handleCategoriesChange}
                placeholder="All channels"
                hasSelectAll
              />
            </div>

            <div className="filter-item">
              <Tooltip content="Parent company that owns the retail brand" side="right">
                <label className="filter-label">
                  Ultimate Parent
                  {retailers.length > 0 && <span className="filter-count">{retailers.length}</span>}
                </label>
              </Tooltip>
              <CustomDropdownWithSearch
                value={retailers}
                options={availableRetailers}
                onChange={handleRetailersChange}
                placeholder="All retailers"
                showTags={false}
              />
            </div>

            <div className="filter-item">
              <Tooltip content="Specific retail brand under the parent company" side="right">
                <label className="filter-label">
                  Brand
                  {brands.length > 0 && <span className="filter-count">{brands.length}</span>}
                </label>
              </Tooltip>
              <CustomDropdownWithSearch
                value={brands}
                options={filteredBrands}
                onChange={handleBrandsChange}
                placeholder={retailers.length === 0 ? 'Select a retailer first' : 'All brands'}
                showTags={false}
              />
            </div>

            {retailers.length > 0 && (
              <div className="neighbourhood-sub-filter">
                <div className="neighbourhood-sub-filter-header">
                  <label className="filter-checkbox-item neighbourhood-toggle">
                    <input
                      type="checkbox"
                      checked={showNeighbourhood}
                      onChange={(e) => {
                        setShowNeighbourhood(e.target.checked);
                      }}
                    />
                    <Tooltip content="Display nearby stores in the selected retailer's neighbourhood" side="right">
                      <span>Show Neighbourhood Stores</span>
                    </Tooltip>
                  </label>
                </div>
                {showNeighbourhood && (
                  <div className="neighbourhood-sub-filter-body">
                    <div className="filter-item">
                      <Tooltip content="Filter neighbourhood stores by retailer" side="right">
                        <label className="filter-label">
                          Retailer
                          {neighbourhoodRetailers.length > 0 && <span className="filter-count">{neighbourhoodRetailers.length}</span>}
                        </label>
                      </Tooltip>
                      <CustomDropdownWithSearch
                        value={neighbourhoodRetailers}
                        options={availableRetailers}
                        onChange={setNeighbourhoodRetailers}
                        placeholder="All retailers"
                        showTags={false}
                      />
                    </div>
                    <div className="filter-item">
                      <Tooltip content="Filter neighbourhood stores by channel" side="right">
                        <label className="filter-label">
                          Channel
                          {neighbourhoodChannels.length > 0 && <span className="filter-count">{neighbourhoodChannels.length}</span>}
                        </label>
                      </Tooltip>
                      <MultiSelectDropdown
                        values={neighbourhoodChannels}
                        options={availableCategories}
                        onChange={setNeighbourhoodChannels}
                        placeholder="All channels"
                        hasSelectAll
                      />
                    </div>
                    <div className="filter-item">
                      <Tooltip content="Filter neighbourhood stores by customer status" side="right">
                        <label className="filter-label">
                          Status
                        </label>
                      </Tooltip>
                      <CustomDropdown
                        value={neighbourhoodStatus}
                        options={NEIGHBOURHOOD_STATUS_OPTIONS}
                        onChange={setNeighbourhoodStatus}
                      />
                    </div>
                    <div className="filter-item">
                      <Tooltip content="Filter neighbourhood stores by installed solutions" side="right">
                        <label className="filter-label">
                          Installed Solutions
                          {neighbourhoodSolutions.length > 0 && <span className="filter-count">{neighbourhoodSolutions.length}</span>}
                        </label>
                      </Tooltip>
                      <MultiSelectDropdown
                        values={neighbourhoodSolutions}
                        options={SOLUTIONS_OPTIONS}
                        onChange={setNeighbourhoodSolutions}
                        placeholder="All solutions"
                        disabledOptions={neighbourhoodWhitespace}
                      />
                    </div>
                    <div className="filter-item">
                      <Tooltip content="Filter neighbourhood stores by whitespace (missing solutions)" side="right">
                        <label className="filter-label">
                          Whitespace
                          {neighbourhoodWhitespace.length > 0 && <span className="filter-count">{neighbourhoodWhitespace.length}</span>}
                        </label>
                      </Tooltip>
                      <MultiSelectDropdown
                        values={neighbourhoodWhitespace}
                        options={SOLUTIONS_OPTIONS}
                        onChange={setNeighbourhoodWhitespace}
                        placeholder="All solutions"
                        disabledOptions={neighbourhoodSolutions}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>

        </div>

        {/* Neighborhood Penetration */}
        <div className="filter-group">
          <button
            className="filter-collapsible-header"
            onClick={() => setIsPenetrationExpanded(!isPenetrationExpanded)}
            type="button"
          >
            <MdChevronRight className={`collapsible-chevron ${isPenetrationExpanded ? 'expanded' : ''}`} size={18} />
            <HiOutlineChartBar className="group-icon" />
            <Tooltip content="Percentage of nearby stores using Gatekeeper solutions" side="right">
              <span className="group-title">Neighborhood Penetration %</span>
            </Tooltip>
          </button>
          {isPenetrationExpanded && (
            <div className="filter-group-body">
              <RangeSlider
                min={penetrationMin}
                max={penetrationMax}
                minLimit={0}
                maxLimit={100}
                onChange={(min, max) => {
                  setPenetrationMin(min);
                  setPenetrationMax(max);
                }}
                suffix="%"
              />
            </div>
          )}
        </div>

        {/* Crime Index */}
        <div className="filter-group">
          <button
            className="filter-collapsible-header"
            onClick={() => setIsCrimeExpanded(!isCrimeExpanded)}
            type="button"
          >
            <MdChevronRight className={`collapsible-chevron ${isCrimeExpanded ? 'expanded' : ''}`} size={18} />
            <HiOutlineShieldCheck className="group-icon" />
            <Tooltip content="Crime rate level in the store's area" side="right">
              <span className="group-title">Crime Index</span>
            </Tooltip>
            {crimeIndex.length > 0 && (
              <span className="group-filter-count">{crimeIndex.length}</span>
            )}
          </button>
          {isCrimeExpanded && (
            <div className="filter-group-body">
              <MultiSelectDropdown
                values={crimeIndex.map(id => CRIME_INDEX_OPTIONS.find(o => o.id === id)?.label || '').filter(Boolean)}
                options={CRIME_INDEX_OPTIONS.map(o => o.label)}
                onChange={(selectedLabels) => {
                  const ids = selectedLabels.map(label => CRIME_INDEX_OPTIONS.find(o => o.label === label)?.id || '').filter(Boolean);
                  setCrimeIndex(ids);
                }}
                placeholder="All levels"
                hasSelectAll
              />
            </div>
          )}
        </div>

        {/* Weather Conditions */}
        <div className="filter-group">
          <button
            className="filter-collapsible-header"
            onClick={() => setIsWeatherExpanded(!isWeatherExpanded)}
            type="button"
          >
            <MdChevronRight className={`collapsible-chevron ${isWeatherExpanded ? 'expanded' : ''}`} size={18} />
            <HiOutlineCloud className="group-icon" />
            <Tooltip content="Filter by local weather patterns" side="right">
              <span className="group-title">Weather Conditions</span>
            </Tooltip>
          </button>
          {isWeatherExpanded && (
            <div className="filter-group-body">
              <div className="filter-item">
                <Tooltip content="Months per year with high temperatures" side="right">
                  <label className="filter-label">No of Hot Months</label>
                </Tooltip>
                <RangeSlider
                  min={hotMonthsMin}
                  max={hotMonthsMax}
                  minLimit={0}
                  maxLimit={12}
                  onChange={(min, max) => {
                    setHotMonthsMin(min);
                    setHotMonthsMax(max);
                  }}
                  suffix=" months"
                />
              </div>
              <div className="filter-item">
                <Tooltip content="Months per year with low temperatures" side="right">
                  <label className="filter-label">No of Cold Months</label>
                </Tooltip>
                <RangeSlider
                  min={coldMonthsMin}
                  max={coldMonthsMax}
                  minLimit={0}
                  maxLimit={12}
                  onChange={(min, max) => {
                    setColdMonthsMin(min);
                    setColdMonthsMax(max);
                  }}
                  suffix=" months"
                />
              </div>
            </div>
          )}
        </div>

        {/* Shopping Complex */}
        <div className="filter-group">
          <button
            className="filter-collapsible-header"
            onClick={() => setIsShoppingExpanded(!isShoppingExpanded)}
            type="button"
          >
            <MdChevronRight className={`collapsible-chevron ${isShoppingExpanded ? 'expanded' : ''}`} size={18} />
            <HiOutlineBuildingOffice2 className="group-icon" />
            <Tooltip content="Filter stores in shopping complexes or plazas" side="right">
              <span className="group-title">Shopping Complex</span>
            </Tooltip>
          </button>
          {isShoppingExpanded && (
            <div className="filter-group-body">
              <label className="filter-checkbox-item">
                <input
                  type="checkbox"
                  checked={shoppingComplexOnly}
                  onChange={(e) => setShoppingComplexOnly(e.target.checked)}
                />
                <Tooltip content="Only show stores located in shopping complexes or plazas" side="right">
                  <span>Shopping Complex Opportunities Only</span>
                </Tooltip>
              </label>
            </div>
          )}
        </div>

        {/* Cart Ordinance */}
        <div className="filter-group">
          <button
            className="filter-collapsible-header"
            onClick={() => setIsCartExpanded(!isCartExpanded)}
            type="button"
          >
            <MdChevronRight className={`collapsible-chevron ${isCartExpanded ? 'expanded' : ''}`} size={18} />
            <HiOutlineShoppingCart className="group-icon" />
            <Tooltip content="Local laws regarding shopping cart retrieval" side="right">
              <span className="group-title">Cart Ordinance</span>
            </Tooltip>
            {cartOrdinance !== 'All' && (
              <span className="group-filter-count">1</span>
            )}
          </button>
          {isCartExpanded && (
            <div className="filter-group-body">
              <CustomDropdown
                value={cartOrdinance}
                options={CART_ORDINANCE_OPTIONS}
                onChange={setCartOrdinance}
              />
            </div>
          )}
        </div>
      </div>

      <div className="filter-actions">
        <button
          className={`apply-filters-btn ${!hasUnappliedChanges ? 'disabled' : ''}`}
          onClick={handleApplyFilters}
          disabled={!hasUnappliedChanges}
        >
          Apply Filters
        </button>
      </div>
    </div>
  );
};

export default Filter;
